import React, { useEffect, useState, useMemo } from "react";
import toast, { Toaster } from 'react-hot-toast';
import AddProductForm from "../components/AddProductForm";
import CheckoutModal from "../components/InventoryModals/CheckoutModal";
import TransferModal from "../components/InventoryModals/TransferModal";
import { fetchInventory, deleteInventory, transferInventory } from "../api/api";
import '../index.css';
import { Container, Table, Button, Form, Modal, Row, Col, Dropdown } from 'react-bootstrap';
import "../ui/itemrows.css"

const Inventory = () => {
  const [products, setProducts] = useState();
  const [selectedIds, setSelectedIds] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false); // Modal state
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [checkoutGroup, setCheckoutGroup] = useState(null);
  const [transferGroup, setTransferGroup] = useState(null);
  const [checkoutQuantity, setCheckoutQuantity] = useState(1);
  const [transferQuantity, setTransferQuantity] = useState(1);
  const [transferSquadronId, setTransferSquadronId] = useState("0");
  const [transferCompartmentId, setTransferCompartmentId] = useState("0");
  const [addAnother, setAddAnother] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({
    itemName: "",
    squadronId: "",
    compartment: "",
    size: ""
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const fetchedProducts = await fetchInventory();
        setProducts(fetchedProducts);
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };

    fetchData();
  }, []);

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    try {
      await deleteInventory(selectedIds);
      toast.success('Selected products deleted successfully');
      setProducts(await fetchInventory());
      setSelectedIds([]);
    } catch (error) {
      toast.error('Error deleting selected products');
    }
  };

  const handleCheckout = (group) => {
    setCheckoutGroup(group);
    setCheckoutQuantity(1);
    setShowCheckoutModal(true);
  };

  const handleTransfer = (group) => {
    setTransferGroup(group);
    setTransferQuantity(1);
    setShowTransferModal(true);
  }

  const confirmCheckout = async () => {
    if (!checkoutGroup || checkoutQuantity < 1) return;
    const idsToDelete = checkoutGroup.inventoryIds.slice(0, checkoutQuantity);
    try {
      await deleteInventory(idsToDelete);
      toast.success('Checked out successfully');
      setProducts(await fetchInventory());
      setShowCheckoutModal(false);
      setCheckoutGroup(null);
    } catch (error) {
      toast.error('Error checking out items');
    }
  };

  const confirmTransfer = async () => {
    if (!transferGroup || transferQuantity < 1) return;
    const idsToTransfer = transferGroup.inventoryIds.slice(0, transferQuantity);
    try {
      // TODO: Create the body
      await transferInventory(idsToTransfer, transferSquadronId, transferCompartmentId);
      toast.success('Checked out successfully');
      setProducts(await fetchInventory());
      setShowTransferModal(false);
      setTransferGroup(null);
    } catch (error) {
      toast.error('Error checking out items');
    }
  };

  // Helper to group products by all columns (item name, squadron ID, compartment, NSN, int_size, str_size, gender)
  const getGroupedProducts = (products) => {
    if (!products) return [];
    const grouped = {};
    products.forEach(product => {
      // Build a strict grouping key with all relevant fields
      const keyObj = {
        itemName: product.item.name,
        squadronId: product.inventory.squad_id,
        compartment: product.compartment.name,
        nsn: product.inventory.nsn_info?.nsn || "",
        nsnSize: product.inventory.nsn_info?.size || "",
        intSize: product.inventory.int_size,
        strSize: product.inventory.str_size,
      };
      const key = JSON.stringify(keyObj);
      if (!grouped[key]) {
        grouped[key] = {
          ...product,
          quantity: 1,
          inventoryIds: [product.inventory.id],
          // Save the grouping fields for later use (optional)
          _groupKey: keyObj
        };
      } else {
        grouped[key].quantity += 1;
        grouped[key].inventoryIds.push(product.inventory.id);
      }
    });
    return Object.values(grouped);
  };

  // Extract unique filter options from products
  const filterOptions = useMemo(() => {
    if (!products) return { itemNames: [], squadronIds: [], compartments: [], sizes: [] };
    const itemNames = new Set();
    const squadronIds = new Set();
    const compartments = new Set();
    const sizes = new Set();
    products.forEach(product => {
      itemNames.add(product.item.name);
      squadronIds.add(product.inventory.squad_id);
      compartments.add(product.compartment.name);
      // Size logic as in table
      let size = product.inventory.nsn_info
        ? `${product.inventory.nsn_info.size} (${product.inventory.nsn_info.nsn})`
        : product.inventory.int_size !== null && product.inventory.int_size !== -1
          ? product.inventory.int_size
          : product.inventory.str_size || "N/A";
      sizes.add(size);
    });
    return {
      itemNames: Array.from(itemNames),
      squadronIds: Array.from(squadronIds),
      compartments: Array.from(compartments),
      sizes: Array.from(sizes)
    };
  }, [products]);

  // Filtering logic
  const filteredProducts = useMemo(() => {
    if (!products) return [];
    return products.filter(product => {
      // Item Name
      if (filters.itemName && product.item.name !== filters.itemName) return false;
      // Squadron ID
      if (filters.squadronId && product.inventory.squad_id !== filters.squadronId) return false;
      // Compartment
      if (filters.compartment && product.compartment.name !== filters.compartment) return false;
      // Size
      let size = product.inventory.nsn_info
        ? `${product.inventory.nsn_info.size} (${product.inventory.nsn_info.nsn})`
        : product.inventory.int_size !== null && product.inventory.int_size !== -1
          ? product.inventory.int_size
          : product.inventory.str_size || "N/A";
      if (filters.size && size !== filters.size) return false;
      return true;
    });
  }, [products, filters]);

  return (
    <Container className="App" style={{ maxWidth: 1200, marginTop: 32 }}>
      <header className="App-header" />
      <Row className="mb-3 align-items-center" style={{ gap: 8 }}>
        <Col xs="auto">
          <Button
            variant="primary"
            onClick={() => setShowAddModal(true)}
            style={{ borderRadius: 6, fontWeight: 500, fontSize: 15, padding: "8px 18px" }}
          >
            Add Inventory
          </Button>
        </Col>
        <Col xs="auto">
          <Button
            variant="danger"
            onClick={handleDeleteSelected}
            disabled={selectedIds.length === 0}
            style={{ borderRadius: 6, fontWeight: 500, fontSize: 15, padding: "8px 18px" }}
          >
            Delete selected
          </Button>
        </Col>
      </Row>
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Inventory</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AddProductForm
            setProducts={setProducts}
            onSuccess={() => {
              if (!addAnother) setShowAddModal(false);
            }}
            showSubmitButton={false}
            addAnother={addAnother}
          />
        </Modal.Body>
        <Modal.Footer>
          <Form.Check
            type="checkbox"
            id="add-another-checkbox"
            label="Add another"
            checked={addAnother}
            onChange={e => setAddAnother(e.target.checked)}
            className="me-auto"
          />
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              // Trigger submit on the AddProductForm
              document.getElementById('add-product-form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
            }}
          >
            Add Product
          </Button>
        </Modal.Footer>
      </Modal>
      <CheckoutModal
        show={showCheckoutModal}
        onHide={() => setShowCheckoutModal(false)}
        checkoutGroup={checkoutGroup}
        checkoutQuantity={checkoutQuantity}
        setCheckoutQuantity={setCheckoutQuantity}
        confirmCheckout={confirmCheckout}
      />
      <TransferModal
        show={showTransferModal}
        onHide={() => setShowTransferModal(false)}
        transferGroup={transferGroup}
        transferQuantity={transferQuantity}
        setTransferQuantity={setTransferQuantity}
        setTransferSquadronId={setTransferSquadronId}
        setTransferCompartmentId={setTransferCompartmentId}
        confirmTransfer={confirmTransfer}
      />
      {/* Filter Controls */}
      <div
        style={{
          background: "#f4f5f7",
          borderRadius: 10,
          padding: "18px 18px 8px 18px",
          marginBottom: 18,
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
        }}
      >
        <Row className="mb-2" style={{ gap: 8 }}>
          <Col xs={12} md={2}>
            <Form.Group>
              <Form.Label style={{ fontWeight: 500, color: "#444" }}>Squadron ID</Form.Label>
              <Form.Select
                value={filters.squadronId}
                onChange={e => setFilters(f => ({ ...f, squadronId: e.target.value }))}
                style={{ borderRadius: 6, fontSize: 15 }}
              >
                <option value="">All</option>
                {filterOptions.squadronIds.map(id => (
                  <option key={id} value={id}>{id}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs={12} md={2}>
            <Form.Group>
              <Form.Label style={{ fontWeight: 500, color: "#444" }}>Item Name</Form.Label>
              <Form.Select
                value={filters.itemName}
                onChange={e => setFilters(f => ({ ...f, itemName: e.target.value }))}
                style={{ borderRadius: 6, fontSize: 15 }}
              >
                <option value="">All</option>
                {filterOptions.itemNames.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs={12} md={2}>
            <Form.Group>
              <Form.Label style={{ fontWeight: 500, color: "#444" }}>Compartment</Form.Label>
              <Form.Select
                value={filters.compartment}
                onChange={e => setFilters(f => ({ ...f, compartment: e.target.value }))}
                style={{ borderRadius: 6, fontSize: 15 }}
              >
                <option value="">All</option>
                {filterOptions.compartments.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs={12} md={2}>
            <Form.Group>
              <Form.Label style={{ fontWeight: 500, color: "#444" }}>Size</Form.Label>
              <Form.Select
                value={filters.size}
                onChange={e => setFilters(f => ({ ...f, size: e.target.value }))}
                style={{ borderRadius: 6, fontSize: 15 }}
              >
                <option value="">All</option>
                {filterOptions.sizes.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs={12} md={2} className="d-flex align-items-end">
            <Button
              variant="outline-secondary"
              onClick={() => setFilters({ itemName: "", squadronId: "", compartment: "", size: "" })}
              style={{ borderRadius: 6, fontSize: 15, fontWeight: 500 }}
            >
              Clear Filters
            </Button>
          </Col>
        </Row>
      </div>
      <div
        className="Inventory"
        style={{
          background: "#fafbfc",
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          padding: 0,
          marginBottom: 32,
          overflow: "hidden"
        }}
      >
        {products ? (
          <div style={{ width: "100%", overflowX: "auto" }}>
            <div className="item-rows" style={{ background: "transparent", padding: 0 }}>
              <Table
                className="mb-0"
                striped
                responsive
                style={{
                  marginBottom: 0,
                  borderRadius: 12,
                  overflow: "hidden",
                  background: "transparent",
                  minWidth: 900,
                  border: "none"
                }}
              >
                <thead style={{
                  background: "#f4f5f7",
                  position: "sticky",
                  top: 0,
                  zIndex: 2
                }}>
                  <tr>
                    <th style={{ width: 36 }}>
                      <Form.Check
                        type="checkbox"
                        checked={
                          getGroupedProducts(products).length > 0 &&
                          getGroupedProducts(products).every(group =>
                            group.inventoryIds.every(id => selectedIds.includes(id))
                          )
                        }
                        onChange={e => {
                          if (e.target.checked) {
                            // Select all inventory IDs
                            const allIds = getGroupedProducts(products).flatMap(g => g.inventoryIds);
                            setSelectedIds(allIds);
                          } else {
                            setSelectedIds([]);
                          }
                        }}
                      />
                    </th>
                    <th style={{ fontWeight: 500, color: "#333", fontSize: 15 }}>Item Name</th>
                    <th style={{ fontWeight: 500, color: "#333", fontSize: 15 }}>Squadron ID</th>
                    <th style={{ fontWeight: 500, color: "#333", fontSize: 15 }}>Compartment</th>
                    <th style={{ fontWeight: 500, color: "#333", fontSize: 15 }}>Size</th>
                    <th style={{ fontWeight: 500, color: "#333", fontSize: 15 }}>Quantity</th>
                    <th style={{ fontWeight: 500, color: "#333", fontSize: 15 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getGroupedProducts(filteredProducts).map((group, idx) => (
                    <tr
                      key={idx}
                      className={group.inventoryIds.some(id => selectedIds.includes(id)) ? "selected" : ""}
                      style={{
                        cursor: "pointer",
                        background: group.inventoryIds.some(id => selectedIds.includes(id)) ? "#e6f0ff" : undefined,
                        transition: "background 0.15s"
                      }}
                      onMouseOver={e => {
                        if (!group.inventoryIds.some(id => selectedIds.includes(id))) {
                          e.currentTarget.style.background = "#f0f4fa";
                        }
                      }}
                      onMouseOut={e => {
                        if (!group.inventoryIds.some(id => selectedIds.includes(id))) {
                          e.currentTarget.style.background = "";
                        }
                      }}
                    >
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={group.inventoryIds.every(id => selectedIds.includes(id))}
                          onChange={e => {
                            if (e.target.checked) {
                              setSelectedIds(prev => [...prev, ...group.inventoryIds.filter(id => !prev.includes(id))]);
                            } else {
                              setSelectedIds(prev => prev.filter(id => !group.inventoryIds.includes(id)));
                            }
                          }}
                        />
                      </td>
                      <td style={{ fontSize: 15, color: "#222" }}>{group.item.name}</td>
                      <td style={{ fontSize: 15, color: "#222" }}>{group.inventory.squad_id}</td>
                      <td style={{ fontSize: 15, color: "#222" }}>{group.compartment.name}</td>
                      <td style={{ fontSize: 15, color: "#222" }}>
                        {group.inventory.size
                          ? group.inventory.size + (group.inventory.nsn_info?.nsn ? ` (${group.inventory.nsn_info.nsn})` : "")
                          : "N/A"}
                      </td>
                      <td style={{ fontSize: 15, color: "#222" }}>{group.quantity}</td>
                      <td>
                        <Dropdown align="end">
                          <Dropdown.Toggle variant="secondary" id={`dropdown-${idx}`} size="sm" style={{ borderRadius: 6 }}>
                            Actions
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={() => handleCheckout(group)}>
                              Checkout
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => handleTransfer(group)}>
                              Transfer
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            {getGroupedProducts(filteredProducts).length === 0 && (
              <div style={{ color: "#888", textAlign: "center", padding: "32px 0", fontSize: 16 }}>
                No inventory found.
              </div>
            )}
          </div>
        ) : (
          <p style={{ color: "#888", textAlign: "center", padding: "32px 0", fontSize: 16 }}>Loading...</p>
        )}
      </div>
      <Toaster
        toastOptions={{
          className: '',
          style: {
            borderRadius: '10px',
            background: '#333',
            color: '#fff',
          },
          position: "bottom-center"
        }}/>
    </Container>
  );
};

export default Inventory;